import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import {
    CalendarDays,
    LayoutDashboard,
    PanelLeftClose,
    PanelLeftOpen,
    Search,
    Sparkles,
    Store,
    UserCircle2,
    UserRound,
    X,
    Users,
    UserSearch
} from 'lucide-react'
import { BASE_URL_API } from '../../References/URLs'
import AuthContext from '../../store/auth-context'
import styles from '../StaffManagement/ManageStaff.module.css'
import { Link, useNavigate } from 'react-router-dom'
import ManageEachStaffWrapper from '../../Components/StaffManagement/ManageEachStaffWrapper'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import DateRangeSelector from '../../Components/UI/DateRangeSelector'
import ManageStaffSummary from './ManageStaffSummary'

const toLocalIsoDate = (date) => (
    new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().substring(0, 10)
)

const getDefaultDateRange = () => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 0)

    return {
        startDate: toLocalIsoDate(start),
        endDate: toLocalIsoDate(end)
    }
}

const getInitials = (name) => {
    const cleanName = String(name || '').trim()
    if (!cleanName) {
        return 'NA'
    }

    const tokens = cleanName.split(/\s+/).filter(Boolean)
    if (tokens.length === 1) {
        return tokens[0].slice(0, 2).toUpperCase()
    }

    return `${tokens[0][0]}${tokens[tokens.length - 1][0]}`.toUpperCase()
}

const ManageStaff = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const navigate = useNavigate()
    const activeStore = (() => { try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const { staffIdentifier, activeTab } = useParams()
    const [dates, setDates] = useState(getDefaultDateRange)
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const [isRailCollapsed, setIsRailCollapsed] = useState(false)
    const [isMobileRailOpen, setIsMobileRailOpen] = useState(false)
    const [searchKey, setSearchKey] = useState('')

    const changeDateHandler = useCallback((startDate, endDate) => {
        setDates((prevDates) => {
            if (prevDates.startDate === startDate && prevDates.endDate === endDate) {
                return prevDates
            }

            return {
                startDate,
                endDate
            }
        })
    }, [])


    const [workers, setWorkers] = useState([])
    const effectiveTab = activeTab || 'profile'

    const selectedRangeLabel = useMemo(() => {
        return `${dates?.startDate || '--'} → ${dates?.endDate || '--'}`
    }, [dates?.endDate, dates?.startDate])

    const filteredWorkers = useMemo(() => {
        const normalizedSearch = searchKey.trim().toLowerCase()
        const baseWorkers = Array.isArray(workers) ? workers : []

        const sortedWorkers = [...baseWorkers].sort((a, b) => {
            return String(a?.name || '').localeCompare(String(b?.name || ''))
        })

        if (!normalizedSearch) {
            return sortedWorkers
        }

        return sortedWorkers.filter((worker) => {
            return String(worker?.name || '').toLowerCase().includes(normalizedSearch)
        })
    }, [searchKey, workers])

    const selectedStaffName = useMemo(() => {
        if (!staffIdentifier || staffIdentifier === 'summary') {
            return 'Summary'
        }

        const matchedStaff = workers.find((worker) => String(worker?.id) === String(staffIdentifier))
        return matchedStaff?.name || 'Staff details'
    }, [staffIdentifier, workers])

    const closeDateModal = () => {
        setIsDatePickerOpen(false)
    }

    const openSummary = () => {
        navigate('/staff/summary')
        setIsMobileRailOpen(false)
    }

    const openStaff = (workerId) => {
        navigate(`/staff/${workerId}/${effectiveTab}`)
        setIsMobileRailOpen(false)
    }

    useEffect(() => {
        const handleWindowResize = () => {
            if (window.innerWidth > 1024) {
                setIsMobileRailOpen(false)
            }
        }

        const handleEscapePress = (event) => {
            if (event.key === 'Escape') {
                setIsMobileRailOpen(false)
            }
        }

        window.addEventListener('resize', handleWindowResize)
        window.addEventListener('keydown', handleEscapePress)

        return () => {
            window.removeEventListener('resize', handleWindowResize)
            window.removeEventListener('keydown', handleEscapePress)
        }
    }, [])

    const getWorkersData = async () => {
        const getWorkersDataResponse = await fetch(BASE_URL_API + "getWorkersData",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier
                })
            });

        if (!getWorkersDataResponse.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const getWorkersDataRespo = await getWorkersDataResponse.json();
            if (getWorkersDataRespo.status === 'success') {
                setWorkers(getWorkersDataRespo.response);
            } else {
                if (getWorkersDataRespo.message === 'tokenExpired') {
                    authCtx.logout()
                } else {
                    console.log(getWorkersDataRespo)
                }
            }
        }
    }

    useEffect(() => {
        getWorkersData()
    }, [activeStore?.storeIdentifier])



    if (activeStore === null) {
        return (
            <div className={styles.noStoreWrapper}>
                <div className={styles.noStoreCard}>
                    <Store size={30} className={styles.noStoreIcon} />
                    <h3>No Store Selected</h3>
                    <p>Please select an active store to access staff analytics and performance details.</p>
                    <Link to="/home" className={styles.noStoreBackBtn}>Go to Dashboard</Link>
                </div>
            </div>
        )
    }



    return (
        <div className={styles.mainWrapper}>
            <DateRangeSelector
                isOpen={isDatePickerOpen}
                closeModal={closeDateModal}
                changeDateHandler={changeDateHandler}
                initialStartDate={dates?.startDate}
                initialEndDate={dates?.endDate}
            />

            <div className={styles.pageHeader}>
                <div className={styles.pageHeaderLeft}>
                    <span className={styles.statusPill}>Staff Management</span>
                    {/* <h1 className={styles.pageTitle}>Manage Team Performance</h1> */}
                    <p className={styles.pageSubtitle}>Track each staff member’s profile, attendance, work insights, and summary analytics in one unified workspace.</p>
                </div>

                <div className={styles.pageHeaderRight}>
                    <div className={styles.statChip}>
                        <Users size={14} />
                        <span>{workers.length} staff</span>
                    </div>
                    <button
                        className={styles.dateRangeBtn}
                        type="button"
                        onClick={() => setIsDatePickerOpen(true)}
                    >
                        <CalendarDays size={14} />
                        <span>{selectedRangeLabel}</span>
                    </button>

                    <button
                        className={styles.mobileRailToggle}
                        type="button"
                        onClick={() => setIsMobileRailOpen(true)}
                    >
                        <Users size={14} />
                        <span>Staff list</span>
                    </button>
                </div>
            </div>

            <div className={`${styles.contentGrid} ${isRailCollapsed ? styles.contentGridCollapsed : ''}`}>
                <aside className={`${styles.staffRail} ${isRailCollapsed ? styles.staffRailCollapsed : ''} ${isMobileRailOpen ? styles.staffRailMobileOpen : ''}`}>
                    <div className={styles.staffRailHeader}>
                        <div className={styles.staffRailTitleWrap}>
                            <span className={styles.staffRailPill}>Navigator</span>
                            {!isRailCollapsed && <h3 className={styles.staffRailTitle}>Staff & Summary</h3>}
                        </div>

                        <div className={styles.staffRailActions}>
                            <button
                                type="button"
                                className={styles.collapseBtn}
                                onClick={() => setIsRailCollapsed((previousState) => !previousState)}
                                title={isRailCollapsed ? 'Expand staff rail' : 'Collapse staff rail'}
                            >
                                {isRailCollapsed ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />}
                            </button>

                            <button
                                type="button"
                                className={styles.mobileCloseBtn}
                                onClick={() => setIsMobileRailOpen(false)}
                                title="Close staff rail"
                            >
                                <X size={15} />
                            </button>
                        </div>
                    </div>

                    <div className={`${styles.searchWrap} ${isRailCollapsed ? styles.searchWrapHidden : ''}`}>
                        <Search size={14} className={styles.searchIcon} />
                        <input
                            value={searchKey}
                            onChange={(event) => setSearchKey(event.target.value)}
                            className={styles.searchInput}
                            placeholder="Search staff"
                            disabled={isRailCollapsed}
                        />
                    </div>

                    <button
                        type="button"
                        onClick={openSummary}
                        className={`${styles.staffNavBtn} ${staffIdentifier === 'summary' ? styles.staffNavBtnActive : ''}`}
                        title="Summary"
                    >
                        {isRailCollapsed
                            ? <LayoutDashboard size={15} />
                            : <LayoutDashboard size={15} />}
                        <span className={styles.navLabel}>Summary</span>
                    </button>

                    <div className={styles.staffNavList}>
                        {filteredWorkers.map((worker) => {
                            const isActiveWorker = String(staffIdentifier) === String(worker?.id)
                            return (
                                <button
                                    key={worker?.id}
                                    type="button"
                                    className={`${styles.staffNavBtn} ${isActiveWorker ? styles.staffNavBtnActive : ''}`}
                                    onClick={() => openStaff(worker?.id)}
                                    title={worker?.name}
                                >
                                    {isRailCollapsed
                                        ? <span className={styles.staffInitials}>{getInitials(worker?.name)}</span>
                                        : <UserRound size={15} />}
                                    <span className={styles.navLabel}>{worker?.name}</span>
                                </button>
                            )
                        })}

                        {!isRailCollapsed && filteredWorkers.length === 0 && (
                            <div className={styles.staffListEmpty}>No staff found for “{searchKey}”.</div>
                        )}
                    </div>
                </aside>

                {isMobileRailOpen && (
                    <button
                        type="button"
                        className={styles.mobileOverlay}
                        onClick={() => setIsMobileRailOpen(false)}
                        aria-label="Close staff navigation"
                    />
                )}

                {staffIdentifier !== undefined
                ? <div className={styles.mainBody}>
                    {/* <div className={styles.contextBar}>
                        <span className={styles.contextPill}>Selected</span>
                        <span className={styles.contextTitle}>{selectedStaffName}</span>
                    </div> */}
                    {staffIdentifier === 'summary' && <ManageStaffSummary dates={dates} />}
                    {staffIdentifier !== 'summary' && <ManageEachStaffWrapper dates={dates} activeTab={effectiveTab} />}
                </div>
                : <div className={styles.mainBody}>
                    <div className={styles.selectAStaff}>
                        <div className={styles.emptyIconWrap}>
                            <UserSearch size={28} />
                        </div>

                        <h3 className={styles.selectTitle}>Select a staff to get started</h3>

                        <p className={styles.selectSubTitle}>Use the left staff navigator to open a profile dashboard or choose summary for store-wide insights.</p>

                        <div className={styles.emptyHints}>
                            <div className={styles.emptyHintChip}>
                                <UserCircle2 size={13} />
                                <span>View profile, attendance, and work tabs</span>
                            </div>
                            <div className={styles.emptyHintChip}>
                                <Sparkles size={13} />
                                <span>Open summary for star performer overview</span>
                            </div>
                        </div>
                    </div>
                </div>
            }
            </div>
        </div>
    )
}

export default ManageStaff